/**
 * Canonical browser observability init — PostHog + Sentry.
 *
 * Applications MUST use this entry and NEVER import posthog-js or
 * @sentry/react directly. Analytics events are sent via the PostHog
 * Capture API (production-supported); identity and feature flags use
 * the PostHog JS SDK. Sentry is initialized inside this module.
 *
 * See Part 22 §118.3 and §120.1 of the Tech Guidelines.
 */

import type { z } from 'zod';
import type { IAnalyticsClient } from './analytics/analytics-client.types';
import type { IErrorClient } from './errors/error-client.types';
import type { EventName } from './analytics/analytics-schema';
import { EventRegistry } from './analytics/analytics-schema';
import { sanitizeEventProps } from './privacy/sanitize';
import { initErrorTracking } from './errors/error-client';
import { createBeforeSend } from './errors/before-send';

// Optional peer deps: only resolved when this entry is bundled (browser apps).
// Apps that use @tonnie/observability/browser must have posthog-js and @sentry/react installed.
import posthog from 'posthog-js';
import * as Sentry from '@sentry/react';

export interface BrowserObservabilityConfig {
  posthogApiKey: string;
  posthogHost: string;
  portal: string;
  env: string;
  release: string;
  sentryDsn: string;
  debug?: boolean;
  tracesSampleRate?: number;
  replaysSessionSampleRate?: number;
  replaysOnErrorSampleRate?: number;
}

function postHogCapture(
  apiKey: string,
  host: string,
  eventName: string,
  properties: Record<string, unknown>,
  distinctId: string,
  sessionId: string | undefined
): void {
  const payload = {
    api_key: apiKey,
    event: eventName,
    properties: {
      ...properties,
      distinct_id: distinctId,
      token: apiKey,
      $session_id: sessionId,
    },
    timestamp: new Date().toISOString(),
  };
  const url = `${host}/capture/`;
  const body = JSON.stringify(payload);
  if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' });
    const sent = navigator.sendBeacon(url, blob);
    if (!sent) {
      fetch(url, {
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
      }).catch(() => {
        /* no-op */
      });
    }
  } else {
    fetch(url, {
      method: 'POST',
      body,
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
    }).catch(() => {
      /* no-op */
    });
  }
}

export function captureCustomEvent(
  eventName: string,
  properties: Record<string, unknown> | undefined,
  helpers: {
    posthogApiKey: string;
    posthogHost: string;
    getDistinctId: () => string;
    getSessionId: () => string | undefined;
    sanitize: (props: Record<string, unknown>) => Record<string, unknown>;
    standardProps: Record<string, unknown>;
  }
): void {
  if (!helpers.posthogApiKey) return;
  const sanitized = helpers.sanitize({
    ...(properties ?? {}),
    ...helpers.standardProps,
  });
  postHogCapture(
    helpers.posthogApiKey,
    helpers.posthogHost,
    eventName.toLowerCase(),
    sanitized,
    helpers.getDistinctId(),
    helpers.getSessionId()
  );
}

export function initObservabilityBrowser(config: BrowserObservabilityConfig): {
  analyticsClient: IAnalyticsClient;
  errorClient: IErrorClient;
  captureCustomEvent: (eventName: string, properties?: Record<string, unknown>) => void;
} {
  const {
    posthogApiKey,
    posthogHost,
    portal,
    env,
    release,
    sentryDsn,
    debug = false,
    tracesSampleRate = 0.1,
    replaysSessionSampleRate = 0.1,
    replaysOnErrorSampleRate = 1.0,
  } = config;

  const standardProps = {
    portal,
    env,
    release,
    source: 'client' as const,
  };

  if (posthogApiKey) {
    posthog.init(posthogApiKey, {
      api_host: posthogHost,
      autocapture: false,
      persistence: 'localStorage',
      capture_pageview: false,
      capture_pageleave: true,
      debug,
      opt_out_capturing_by_default: false,
      person_profiles: 'always',
      loaded: () => {
        if (debug) {
          console.log('[Observability] PostHog SDK loaded (sessions, flags, identity)');
        }
      },
    } as Record<string, unknown>);
  }

  // "Share Analytics" (Settings → Preferences). Starts from what this device
  // last chose — PostHog persists its opt-out — so a browser whose person opted
  // out is silent from its first request, before their settings have loaded.
  // Every capture path checks it: `track`, `identify` and `captureCustomEvent`
  // POST to PostHog directly, so PostHog's own opt-out does not cover them.
  let consent = posthogApiKey ? !posthog.has_opted_out_capturing() : true;

  const analyticsClient: IAnalyticsClient = {
    track<E extends EventName>(event: E, properties: z.infer<(typeof EventRegistry)[E]>): void {
      if (!consent) return;
      try {
        const schema = EventRegistry[event];
        const validated = schema.parse(properties);
        const sanitized = sanitizeEventProps({
          ...(validated as Record<string, unknown>),
          ...standardProps,
        });
        if (debug) {
          console.log(`[Observability] track("${event}")`, sanitized);
        }
        const distinctId = posthog.get_distinct_id?.() ?? 'anonymous';
        const sessionId = posthog.get_session_id?.();
        postHogCapture(
          posthogApiKey,
          posthogHost,
          event.toLowerCase(),
          sanitized,
          distinctId,
          sessionId
        );
      } catch (err) {
        console.error(`[Observability] track("${event}") FAILED:`, err);
      }
    },
    identify(userId: string, traits: Record<string, unknown>): void {
      if (!consent) return;
      if (debug) console.log('[Observability] identify("' + userId + '")');
      if (posthogApiKey) {
        posthog.identify(userId, sanitizeEventProps(traits));
        const sessionId = posthog.get_session_id?.();
        postHogCapture(
          posthogApiKey,
          posthogHost,
          '$identify',
          { $set: sanitizeEventProps(traits), distinct_id: userId },
          userId,
          sessionId
        );
      }
    },
    group(groupType: string, groupId: string, traits?: Record<string, unknown>): void {
      if (consent && posthogApiKey) {
        posthog.group(groupType, groupId, traits ? sanitizeEventProps(traits) : undefined);
      }
    },
    setPersonProperty(key: string, value: unknown): void {
      if (consent && posthogApiKey) {
        posthog.setPersonProperties({ [key]: value });
      }
    },
    reset(): void {
      if (posthogApiKey) {
        posthog.reset();
      }
    },
    isFeatureEnabled(flag: string): boolean {
      return posthogApiKey ? (posthog.isFeatureEnabled(flag) ?? false) : false;
    },
    getFeatureFlag(flag: string): string | boolean | undefined {
      return posthogApiKey ? (posthog.getFeatureFlag(flag) ?? undefined) : undefined;
    },
    setConsent(granted: boolean): void {
      consent = granted;
      if (!posthogApiKey) return;
      // Persisted by PostHog, and opting out also stops its session recording.
      const optedOut = posthog.has_opted_out_capturing();
      if (granted && optedOut) posthog.opt_in_capturing();
      if (!granted && !optedOut) posthog.opt_out_capturing();
    },
    hasConsent(): boolean {
      return consent;
    },
  };

  const errorClient = initErrorTracking({
    dsn: sentryDsn,
    environment: env,
    release,
    tracesSampleRate,
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    sdk: Sentry,
    // `initErrorTracking` has always accepted this and NO app ever passed one,
    // so nothing was classified before it became an alert. See before-send.ts
    // for the curated list and the rules for editing it.
    beforeSend: createBeforeSend(),
  });

  const captureCustomEventBound = (eventName: string, properties?: Record<string, unknown>) => {
    if (!consent) return;
    captureCustomEvent(eventName, properties, {
      posthogApiKey,
      posthogHost,
      getDistinctId: () => posthog.get_distinct_id?.() ?? 'anonymous',
      getSessionId: () => posthog.get_session_id?.(),
      sanitize: sanitizeEventProps,
      standardProps,
    });
  };

  return { analyticsClient, errorClient, captureCustomEvent: captureCustomEventBound };
}
